import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { patrollerService } from "../../Api/Services/PatrollerService";
import { riderService } from "../../Api/Services/RiderService";
import { useLoading } from "../../Context/LoadingProvider";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { RiderModel } from "../../Api/Models/rider.model";
import ImageInput from "../../Components/ImageInput";
import { HomeIcon } from "../../Components/Icons/HomeIcon";
import { TextDeleteIcon } from "../../Components/Icons/TextDeleteIcon";
import { LongArrowRightIcon } from "../../Components/Icons/LongArrowRightIcon";
import TalabatLogo from "../../Assets/gfx/talabat.svg";
import UserNavigation from "../UserNavigation";

type Step = "photo" | "search";

const AICheckStart: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { startLoading, finnishLoading } = useLoading();
    const { showInfo } = useInfoModal();

    const [step, setStep] = useState<Step>("photo");
    const [photoFiles, setPhotoFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadToken, setUploadToken] = useState<string | null>(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);

    const [riderId, setRiderId] = useState("");
    const [showInvalidRiderId, setShowInvalidRiderId] = useState(false);

    useEffect(() => {
        const photo = photoFiles[0];
        if (!photo || uploading || uploadToken) return;

        setPhotoPreviewUrl(URL.createObjectURL(photo));
        setUploading(true);

        const capturedAt = new Date().toISOString();

        const upload = (latitude: number | null, longitude: number | null) =>
            patrollerService
                .uploadAiPhoto(photo, { latitude, longitude, capturedAt })
                .then((res) => {
                    setUploadToken(res.token);
                })
                .catch(() => {
                    showInfo(t("Photo upload failed. Please try again."));
                    setPhotoFiles([]);
                    setPhotoPreviewUrl(null);
                })
                .finally(() => setUploading(false));

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => upload(pos.coords.latitude, pos.coords.longitude),
                () => upload(null, null),
                { timeout: 5000, maximumAge: 60000 },
            );
        } else {
            upload(null, null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photoFiles]);

    const goToSearch = () => {
        if (!uploadToken) return;
        setStep("search");
    };

    const addDigit = (x: string | number) => setRiderId((p) => p + x.toString());
    const removeDigit = () => {
        setRiderId((p) => p.substring(0, Math.max(p.length - 1, 0)));
        setShowInvalidRiderId(false);
    };

    const fetchRider = (e: React.FormEvent) => {
        e.preventDefault();
        if (!uploadToken) return;

        startLoading("ai-start-search");
        riderService
            .search(riderId)
            .then((riders: RiderModel[]) => {
                if (riders.length === 1) {
                    setShowInvalidRiderId(false);
                    navigate(`/rider/${riders[0].riderId}/ai-check`, {
                        state: {
                            riderModel: riders[0],
                            uploadToken,
                            photoPreviewUrl,
                        },
                    });
                } else {
                    setShowInvalidRiderId(true);
                }
            })
            .finally(() => finnishLoading("ai-start-search"));
    };

    const numpadButtons = Array.from({ length: 9 }, (_, i) => i + 1);

    return (
        <div className="view-container view-container--stretch background">
            <div className="view-content h-full flex flex-col justify-between">
                <div>
                    <div className="navigationbar mb-6">
                        <div className="navigationbar__left">
                            <Link to="/menu" className="navigationbar__button">
                                <HomeIcon />
                            </Link>
                        </div>
                        <div className="navigationbar__center">
                            <img src={TalabatLogo} width="95" height="20" style={{ minWidth: "95px" }} alt="Talabat" />
                        </div>
                        <div className="navigationbar__right">
                            <UserNavigation />
                        </div>
                    </div>
                </div>

                <div className="inner-view-container background-white top-border-radius">
                    <div className="inner-view-content">

                        {step === "photo" && (
                            <>
                                <div className="heading text-center mb-2">{t("AI Check")}</div>
                                <div className="text-light text-center mb-4">
                                    {t("Take a photo of the rider first")}
                                </div>

                                <ImageInput
                                    images={photoFiles}
                                    setImages={setPhotoFiles}
                                    title={t("Take a photo")}
                                    infoText={t("Rider photo")}
                                    multiple={false}
                                    required={false}
                                />

                                {uploading && (
                                    <div style={{ textAlign: "center", marginTop: "12px", color: "var(--mute, #888)", fontSize: "13px" }}>
                                        ⏳ {t("Uploading & starting AI analysis...")}
                                    </div>
                                )}

                                {uploadToken && !uploading && (
                                    <div style={{ textAlign: "center", marginTop: "12px", color: "var(--green, #22c55e)", fontSize: "13px" }}>
                                        ✓ {t("AI analysis started")}
                                    </div>
                                )}

                                <button
                                    className="button mt-6 w-full"
                                    disabled={!uploadToken || uploading}
                                    onClick={goToSearch}
                                >
                                    {t("Continue to Rider Search")}
                                    <LongArrowRightIcon />
                                </button>
                            </>
                        )}

                        {step === "search" && (
                            <>
                                <div className="heading text-center mb-2">{t("Rider Search")}</div>
                                <div className="text-light text-center mb-4" style={{ fontSize: "12px", color: "var(--green, #22c55e)" }}>
                                    🧠 {t("AI is analyzing the photo in the background...")}
                                </div>

                                <form className="flex flex-col items-center" onSubmit={fetchRider}>
                                    <div className="relative mb-2 w-full">
                                        <input
                                            className={`input-field input-field--line text-center ${showInvalidRiderId ? "input-field--error" : ""}`}
                                            type="number"
                                            step={1}
                                            min={0}
                                            value={riderId}
                                            placeholder="Rider ID"
                                            onChange={(e) => {
                                                setShowInvalidRiderId(false);
                                                setRiderId(e.target.value);
                                            }}
                                        />
                                        <div className="input-icon--right" onClick={removeDigit}>
                                            <TextDeleteIcon />
                                        </div>
                                        {showInvalidRiderId && (
                                            <div className="input-field-error-bubble">{t("Invalid Rider ID")}</div>
                                        )}
                                    </div>

                                    <div className="numpad mb-4 w-full">
                                        {numpadButtons.map((n) => (
                                            <div className="numpad__button__container" key={n}>
                                                <div className="numpad__button" onClick={() => addDigit(n)}>
                                                    {n}
                                                </div>
                                            </div>
                                        ))}
                                        <div />
                                        <div className="numpad__button__container">
                                            <div className="numpad__button" onClick={() => addDigit("0")}>0</div>
                                        </div>
                                    </div>

                                    <div className="w-full mb-6">
                                        <button
                                            type="submit"
                                            className="button w-full"
                                            disabled={showInvalidRiderId || !riderId}
                                        >
                                            {t("Search")}
                                            <LongArrowRightIcon />
                                        </button>
                                    </div>
                                </form>

                                <button
                                    type="button"
                                    className="button button--outline w-full"
                                    style={{ marginBottom: "8px" }}
                                    onClick={() => setStep("photo")}
                                >
                                    ← {t("Back")}
                                </button>
                            </>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default AICheckStart;
